import {useMutation, useQuery, useQueryClient, type QueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type Valoraciones = components['schemas']['ValoracionesRead']
export type Valoracion = components['schemas']['ValoracionRead']
export type Exigencia = components['schemas']['ExigenciaRead']
export type NormaValoracion = components['schemas']['NormaRead']
export type ValoracionWrite = components['schemas']['ValoracionWrite']
export type ValoracionClase = components['schemas']['ValoracionClase']

// El pie usa el `page_size` que contestó el servidor, no esta constante.
const VALORACIONES_PAGE_SIZE = 20

/** Las exigencias no paginan: viajan iguales en todas las páginas y se calculan sobre el
 * historial completo. */
export function useValoraciones(businessId: string | undefined, employeeId: string | null, page = 1) {
  return useQuery({
    queryKey: ['valoraciones', businessId, employeeId, page],
    queryFn: async () =>
      (
        await api.get<Valoraciones>(`/businesses/${businessId}/employees/${employeeId}/valoraciones`, {
          params: {page, page_size: VALORACIONES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && !!employeeId,
    placeholderData: (previous) => previous,
  })
}

/** La respuesta es siempre la PRIMERA página: se guarda bajo esa clave y se invalidan las demás. */
function tocadasPor(queryClient: QueryClient, businessId: string, employeeId: string, data: Valoraciones) {
  queryClient.setQueryData(['valoraciones', businessId, employeeId, 1], data)
  // Todas menos la página 1: invalidar el prefijo entero volvería a pedir la respuesta
  // recién guardada.
  queryClient.invalidateQueries({
    queryKey: ['valoraciones', businessId, employeeId],
    predicate: (query) => query.queryKey[3] !== 1,
  })
  queryClient.invalidateQueries({queryKey: ['examenes-matriz', businessId]})
  queryClient.invalidateQueries({queryKey: ['evidence', businessId]})
}

export function useRegistrarValoracion(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ValoracionWrite) =>
      (await api.post<Valoraciones>(`/businesses/${businessId}/employees/${employeeId}/valoraciones`, input)).data,
    onSuccess: (data) => {
      tocadasPor(queryClient, businessId, employeeId, data)
    },
  })
}

/** Anular no borra: la fila sigue visible con su motivo y deja de contar. */
export function useAnularValoracion(businessId: string, employeeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({valoracionId, motivo}: {valoracionId: string; motivo: string}) =>
      (
        await api.post<Valoraciones>(
          `/businesses/${businessId}/employees/${employeeId}/valoraciones/${valoracionId}/anular`,
          {motivo}
        )
      ).data,
    onSuccess: (data) => {
      tocadasPor(queryClient, businessId, employeeId, data)
    },
  })
}
